    "use client";

import { useEffect, useState, useRef } from 'react';
import bannerData from '../../data/hero.json';

const PARTICLES = [
    '01','10','{}','//','0x','CTF','SSH','XSS',
    'AES','RSA','>_','#!/','&&','VPN','SQL','>>',
];

function useParticles(count = 16) {
    const [particles, setParticles] = useState([]);
    useEffect(() => {
        setParticles(Array.from({ length: count }, (_, i) => ({
            id: i,
            text: PARTICLES[i % PARTICLES.length],
            left: Math.random() * 100,
            top: Math.random() * 100,
            duration: 10 + Math.random() * 14,
            delay: Math.random() * 12,
            size: 9 + Math.random() * 6,
            opacity: 0.05 + Math.random() * 0.1,
        })));
    }, [count]);
    return particles;
}

export default function BannerOne() {
    const [time, setTime] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    const particles = useParticles(16);
    const canvasRef = useRef(null);

    useEffect(() => {
        const tick = () => {
            if (!bannerData.countdownDate) return setTime({ days: 0, hours: 0, minutes: 0, seconds: 0 });
            const d = new Date(bannerData.countdownDate).getTime() - Date.now();
            if (isNaN(d) || d < 0) return setTime({ days: 0, hours: 0, minutes: 0, seconds: 0 });
            setTime({
                days:    Math.floor(d / 86400000),
                hours:   Math.floor((d % 86400000) / 3600000),
                minutes: Math.floor((d % 3600000) / 60000),
                seconds: Math.floor((d % 60000) / 1000),
            });
        };
        tick();
        const t = setInterval(tick, 1000);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        let raf;
        const nodes = [];
        const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
        resize();
        window.addEventListener('resize', resize);
        for (let i = 0; i < 18; i++) nodes.push({
            x: Math.random() * canvas.width, y: Math.random() * canvas.height,
            vx: (Math.random() - 0.5) * 0.3, vy: (Math.random() - 0.5) * 0.3,
            r: 1 + Math.random() * 1.5,
        });
        const draw = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            nodes.forEach(n => {
                n.x += n.vx; n.y += n.vy;
                if (n.x < 0 || n.x > canvas.width) n.vx *= -1;
                if (n.y < 0 || n.y > canvas.height) n.vy *= -1;
            });
            for (let i = 0; i < nodes.length; i++)
                for (let j = i + 1; j < nodes.length; j++) {
                    const dx = nodes[i].x - nodes[j].x, dy = nodes[i].y - nodes[j].y;
                    const d = Math.sqrt(dx*dx + dy*dy);
                    if (d < 140) {
                        ctx.beginPath();
                        ctx.strokeStyle = `rgba(178,13,93,${(1-d/140)*0.12})`;
                        ctx.lineWidth = 0.6;
                        ctx.moveTo(nodes[i].x, nodes[i].y);
                        ctx.lineTo(nodes[j].x, nodes[j].y);
                        ctx.stroke();
                    }
                }
            nodes.forEach(n => {
                ctx.beginPath(); ctx.arc(n.x, n.y, n.r, 0, Math.PI*2);
                ctx.fillStyle = 'rgba(178,13,93,0.35)'; ctx.fill();
            });
            raf = requestAnimationFrame(draw);
        };
        draw();
        return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
    }, []);

    const pad = n => String(n).padStart(2, '0');

    return (
        <>
        <style>{`
            /* ═══════════ HERO ═══════════ */
            .bh {
                position: relative;
                min-height: 100vh;
                display: flex;
                align-items: center;
                overflow: hidden;
                padding: 130px 0 80px;
            }
            .bh__bg {
                position: absolute; inset: 0;
                background-size: cover;
                background-position: center;
                z-index: 0;
                width: 100% !important;
                height: 100% !important;
                background-color: #000;
            }
            .bh__overlay {
                display: none;
            }
            .bh__canvas {
                position: absolute; inset: 0;
                width: 100%; height: 100%;
                z-index: 2; pointer-events: none;
            }
            .bh__particle {
                position: absolute;
                font-family: 'Courier New', monospace;
                color: var(--eventflow-base);
                font-weight: 700;
                pointer-events: none;
                z-index: 2;
                animation: bhFloat linear infinite;
                user-select: none;
            }
            @keyframes bhFloat {
                0%,100% { transform: translateY(0) rotate(0deg); }
                50%     { transform: translateY(-28px) rotate(10deg); }
            }
            .bh__scan {
                position: absolute; left: 0; right: 0; height: 1px;
                background: linear-gradient(90deg,
                    transparent 0%, rgba(178,13,93,0.3) 30%,
                    rgba(178,13,93,0.75) 50%, rgba(178,13,93,0.3) 70%, transparent 100%);
                z-index: 3; pointer-events: none;
                animation: bhScan 7s linear infinite; opacity: 0.4;
            }
            @keyframes bhScan { 0%{top:0%} 100%{top:100%} }
            .bh__orb {
                position: absolute; border-radius: 50%;
                pointer-events: none; z-index: 1; filter: blur(90px);
                animation: bhOrb ease-in-out infinite alternate;
            }
            .bh__orb--1 { width:480px;height:480px;top:-120px;left:-80px;background:rgba(74,10,180,0.13);animation-duration:9s; }
            .bh__orb--2 { width:360px;height:360px;bottom:-60px;right:5%;background:rgba(178,13,93,0.09);animation-duration:7s; }
            @keyframes bhOrb {
                0%   { transform:scale(1) translate(0,0); opacity:.6; }
                100% { transform:scale(1.2) translate(16px,16px); opacity:1; }
            }

            /* ── grid ── */
            .bh__wrap {
                position: relative; z-index: 4;
                display: grid;
                grid-template-columns: 1fr 400px;
                gap: 24px;
                align-items: center;
                max-width: 900px;
            }

            /* ══ LEFT ══ */
            .bh__meta {
                display: flex; align-items: center; gap: 12px;
                margin-bottom: 28px; flex-wrap: wrap;
            }
            .bh__badge {
                display: inline-flex; align-items: center; gap: 7px;
                background: var(--eventflow-base); color: #fff;
                font-family: var(--eventflow-font); font-size: 13px; font-weight: 600;
                padding: 7px 18px; border-radius: 50px;
                box-shadow: 0 6px 20px rgba(178,13,93,0.35);
                animation: bhBadge 3.5s ease-in-out infinite;
            }
            @keyframes bhBadge {
                0%,100% { box-shadow:0 6px 20px rgba(178,13,93,0.25); }
                50%     { box-shadow:0 6px 32px rgba(178,13,93,0.55); }
            }
            .bh__badge i { font-size: 11px; }
            .bh__venue {
                display: inline-flex; align-items: center; gap: 6px;
                font-family: var(--eventflow-font); font-size: 13px;
                color: rgba(255,255,255,0.45);
            }
            .bh__venue i { color: var(--eventflow-base); font-size: 11px; }

            /* title */
            .bh__title {
                margin: 0 0 22px;
                line-height: 1;
            }
            .bh__t1 {
                display: block;
                font-family: var(--eventflow-font);
                font-size: 88px; font-weight: 900;
                color: #fff; letter-spacing: -3px;
                text-transform: uppercase;
            }
            .bh__t2 {
                display: block;
                font-family: var(--eventflow-font);
                font-size: 52px; font-weight: 900;
                color: rgba(255,255,255,0.45);
                letter-spacing: 10px;
                text-transform: uppercase;
                margin-top: 2px;
            }
            .bh__t3 {
                display: block;
                font-family: var(--eventflow-font);
                font-size: 96px; font-weight: 900;
                letter-spacing: -4px;
                background: linear-gradient(135deg, var(--eventflow-base) 0%, #ff4d9e 50%, var(--eventflow-primary) 100%);
                -webkit-background-clip: text;
                -webkit-text-fill-color: transparent;
                background-clip: text;
                line-height: 1;
                margin-top: 4px;
            }

            .bh__sub {
                font-family: var(--eventflow-font);
                font-size: 17px; font-weight: 500;
                color: rgba(255,255,255,0.65);
                margin-bottom: 8px; line-height: 1.5;
            }
            .bh__desc {
                font-family: var(--eventflow-font);
                font-size: 14px; color: rgba(255,255,255,0.38);
                margin-bottom: 36px; line-height: 1.75;
                max-width: 500px;
            }
            .bh__btns {
                display: flex; gap: 14px;
                flex-wrap: wrap; margin-bottom: 32px;
            }
            .bh__tracks {
                display: flex; flex-wrap: wrap; gap: 8px;
            }
            .bh__track {
                font-family: 'Courier New', monospace;
                font-size: 11px; font-weight: 700;
                color: #ff4d9e;
                background: rgba(178,13,93,0.15);
                border: 1px solid rgba(178,13,93,0.3);
                border-radius: 4px; padding: 4px 10px;
                letter-spacing: 0.5px;
                transition: all 0.25s;
            }
            .bh__track:hover { background:rgba(178,13,93,0.4); color:#fff; }

            /* ══ RIGHT CARD ══ */
            .bh__card {
                background: rgba(10,4,20,0.7);
                border: 1px solid rgba(255,255,255,0.08);
                border-radius: 16px;
                overflow: hidden;
                backdrop-filter: blur(20px);
                box-shadow:
                    0 32px 80px rgba(0,0,0,0.55),
                    0 0 0 1px rgba(178,13,93,0.07),
                    inset 0 1px 0 rgba(255,255,255,0.05);
                margin-right: auto;
                margin-left: 0;
            }
            /* terminal bar */
            .bh__card-bar {
                background: rgba(255,255,255,0.04);
                border-bottom: 1px solid rgba(255,255,255,0.06);
                padding: 11px 16px;
                display: flex; align-items: center; gap: 6px;
            }
            .bh__dot { width:10px;height:10px;border-radius:50%; }
            .bh__dot--r{background:#ff5f57;} .bh__dot--y{background:#febc2e;} .bh__dot--g{background:#28c840;}
            .bh__card-bar-title {
                font-family:'Courier New',monospace; font-size:11px;
                color:rgba(255,255,255,0.28); margin-left:6px;
            }
            /* countdown */
            .bh__cd-wrap { padding: 20px 24px 16px; }
            .bh__cd-eyebrow {
                display: flex; align-items: center; gap: 7px;
                font-family: var(--eventflow-font);
                font-size: 10px; font-weight: 700;
                letter-spacing: 2.5px; text-transform: uppercase;
                color: rgba(255,255,255,0.3); margin-bottom: 16px;
            }
            .bh__cd-dot {
                width:6px;height:6px;border-radius:50%;
                background:var(--eventflow-base);
                animation:bhCdDot 1.8s ease-in-out infinite;
            }
            @keyframes bhCdDot {
                0%,100%{opacity:1;transform:scale(1);}
                50%{opacity:.2;transform:scale(1.8);}
            }
            .bh__cd-row {
                display: grid;
                grid-template-columns: repeat(4,1fr);
                gap: 8px;
            }
            .bh__cd-box {
                background: rgba(255,255,255,0.04);
                border: 1px solid rgba(255,255,255,0.07);
                border-radius: 10px;
                padding: 14px 8px 10px;
                text-align: center;
                transition: border-color 0.3s, background 0.3s;
            }
            .bh__cd-box:hover {
                border-color: rgba(178,13,93,0.3);
                background: rgba(178,13,93,0.05);
            }
            .bh__cd-num {
                display: block;
                font-family: var(--eventflow-font);
                font-size: 36px; font-weight: 800;
                color: #fff; line-height: 1;
                letter-spacing: -1px; margin-bottom: 5px;
            }
            .bh__cd-label {
                display: block;
                font-family: var(--eventflow-font);
                font-size: 9px; font-weight: 700;
                letter-spacing: 2px; text-transform: uppercase;
                color: rgba(255,255,255,0.28);
            }
            /* divider */
            .bh__card-div { height:1px; background:rgba(255,255,255,0.06); margin:0 24px; }
            /* stats */
            .bh__stats {
                display: grid; grid-template-columns: 1fr 1fr;
            }
            .bh__stat {
                display: flex; flex-direction: column; align-items: center;
                padding: 18px 12px;
                border-right: 1px solid rgba(255,255,255,0.05);
                border-bottom: 1px solid rgba(255,255,255,0.05);
                transition: background 0.25s;
            }
            .bh__stat:nth-child(2n)  { border-right: none; }
            .bh__stat:nth-child(3),
            .bh__stat:nth-child(4)   { border-bottom: none; }
            .bh__stat:hover { background: rgba(178,13,93,0.05); }
            .bh__stat i { font-size:15px; color:var(--eventflow-base); margin-bottom:6px; }
            .bh__stat-val {
                font-family: var(--eventflow-font);
                font-size: 20px; font-weight: 800;
                color: #fff; line-height: 1; margin-bottom: 3px;
            }
            .bh__stat-lbl {
                font-family: var(--eventflow-font);
                font-size: 9px; font-weight: 700;
                letter-spacing: 1.5px; text-transform: uppercase;
                color: rgba(255,255,255,0.28);
            }
            /* card cta */
            .bh__card-cta-wrap { padding: 20px 24px 24px; }
            .bh__card-cta {
                display: flex !important;
                width: 100%;
                justify-content: center;
                border-radius: 10px !important;
            }

            /* ── RESPONSIVE ── */
            @media (max-width: 1199px) {
                .bh__wrap { grid-template-columns: 1fr 360px; gap: 40px; }
                .bh__t1 { font-size: 72px; }
                .bh__t2 { font-size: 42px; letter-spacing: 6px; }
                .bh__t3 { font-size: 80px; }
            }
            @media (max-width: 991px) {
                .bh__wrap { grid-template-columns: 1fr; gap: 48px; }
                .bh__t1 { font-size: 64px; }
                .bh__t2 { font-size: 36px; letter-spacing: 4px; }
                .bh__t3 { font-size: 72px; }
                .bh__card { max-width: 480px; margin: 0 auto; margin-left: auto; }
            }
            @media (max-width: 576px) {
                .bh { padding: 110px 0 60px; }
                .bh__t1 { font-size: 48px; letter-spacing: -2px; }
                .bh__t2 { font-size: 26px; letter-spacing: 2px; }
                .bh__t3 { font-size: 56px; }
                .bh__cd-num { font-size: 28px; }
                .bh__btns { flex-direction: row; flex-wrap: nowrap; gap: 10px; }
            }
        `}</style>

        <section id="th-home" className="bh" style={{backgroundColor:'#04000A', backgroundImage:'none'}}>
            <div className="bh__bg banner-one__bg jarallax" data-jarallax data-speed="0.15"
                style={{ backgroundImage: `url('${process.env.PUBLIC_URL}/hero%20background.jpeg')`, filter: 'brightness(1.6) saturate(1.1)' }} />
            <div className="bh__overlay" />
            <canvas ref={canvasRef} className="bh__canvas" />
            {particles.map(p => (
                <span key={p.id} className="bh__particle" style={{
                    left:`${p.left}%`, top:`${p.top}%`,
                    fontSize:`${p.size}px`, opacity:p.opacity,
                    animationDuration:`${p.duration}s`, animationDelay:`${p.delay}s`,
                }}>{p.text}</span>
            ))}
            <div className="bh__scan" />
            <div className="bh__orb bh__orb--1" />
            <div className="bh__orb bh__orb--2" />

            <div className="container">
                <div className="bh__wrap">

                    {/* ── LEFT ── */}
                    <div className="bh__left">
                        <div className="bh__meta wow fadeInUp" data-wow-delay="50ms">
                            <span className="bh__badge"><i className="fa fa-calendar-alt" />{bannerData.date}</span>
                            <span className="bh__venue"><i className="fa fa-map-marker-alt" />{bannerData.venue}</span>
                        </div>

                        <h1 className="bh__title wow fadeInUp" data-wow-delay="100ms">
                            <span className="bh__t1">{bannerData.titleLine1 || 'BSIDES'}</span>
                            <span className="bh__t2">{bannerData.titleLine2 || 'COIMBATORE'}</span>
                            <span className="bh__t3">{bannerData.titleLine3 || '2027'}</span>
                        </h1>

                        <p className="bh__sub wow fadeInUp" data-wow-delay="180ms">
                            {bannerData.subtitle}
                        </p>
                        <p className="bh__desc wow fadeInUp" data-wow-delay="220ms">
                            {bannerData.address}
                        </p>

                        <div className="bh__btns wow fadeInUp" data-wow-delay="280ms">
                            {(bannerData.buttons || []).map((btn, i) => (
                                <a key={i} href={btn.link} className={btn.style === 'primary' ? 'thm-btn' : 'thm-btn-outline'}>
                                    {btn.text} <span className="icon-arrow-right" />
                                </a>
                            ))}
                        </div>

                        <div className="bh__tracks wow fadeInUp" data-wow-delay="340ms">
                            {bannerData.tracks.map((t,i) => (
                                <span className="bh__track" key={i}>{t}</span>
                            ))}
                        </div>
                    </div>

                    {/* ── RIGHT CARD ── */}
                    <div className="wow fadeInRight" data-wow-delay="200ms">
                        <div className="bh__card">
                            <div className="bh__card-bar">
                                <span className="bh__dot bh__dot--r" />
                                <span className="bh__dot bh__dot--y" />
                                <span className="bh__dot bh__dot--g" />
                                <span className="bh__card-bar-title">event_countdown.sh</span>
                            </div>

                            {bannerData.countdownDate && (
                                <>
                                    <div className="bh__cd-wrap">
                                        <div className="bh__cd-eyebrow">
                                            <span className="bh__cd-dot" /> {bannerData.countdownLabel || 'Event Starts In'}
                                        </div>
                                        <div className="bh__cd-row">
                                            {[
                                                { val: pad(time.days),    lbl: 'Days' },
                                                { val: pad(time.hours),   lbl: 'Hours' },
                                                { val: pad(time.minutes), lbl: 'Mins' },
                                                { val: pad(time.seconds), lbl: 'Secs' },
                                            ].map((item, i) => (
                                                <div className="bh__cd-box" key={i}>
                                                    <span className="bh__cd-num">{item.val}</span>
                                                    <span className="bh__cd-label">{item.lbl}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="bh__card-div" />
                                </>
                            )}

                            <div className="bh__stats">
                                {(bannerData.stats || []).map((s,i) => (
                                    <div className="bh__stat" key={i}>
                                        <i className={`fa ${s.icon}`} />
                                        <span className="bh__stat-val">{s.value}</span>
                                        <span className="bh__stat-lbl">{s.label}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="bh__card-cta-wrap">
                                <a href={bannerData.buttonLink || '/bsides-contact'} className="bh__card-cta thm-btn">
                                    {bannerData.cardCtaText || 'Secure Your Spot'} <span className="icon-arrow-right" />
                                </a>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </section>
        </>
    );
}
